import React from 'react';
import { useTranslation } from "react-i18next";  
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, CartesianAxis, ReferenceLine } from 'recharts';

interface CashFlowData {
  year: number;
  operatingCashFlow: number;
  investmentCashFlow: number;
  financingCashFlow: number;
  cashFlowLeveredPreTax: number;
  cashFlowLeveredPostTax: number;
  cumulativeCashFlow: number;
}

interface CashFlowChartProps {
  data: CashFlowData[];
  title?: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const formatCurrencyShort = (value: number) => {
  if (Math.abs(value) >= 1000000) {
    return `${(value / 1000000).toFixed(1)}m`;
  } else if (Math.abs(value) >= 1000) {
    return `${(value / 1000).toFixed(0)}k`;
  }
  return formatCurrency(value);
};

const BAR_NAMES: Record<string, string> = {
  cashFlowLeveredPreTax: "CF to equity (pre-tax)",
  cashFlowLeveredPostTax: "CF to equity (post-tax)",
};

export default function CashFlowChart({ data }: CashFlowChartProps) {

  const { t } = useTranslation();
  
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-border p-8">
        <h3 className="text-base font-semibold mb-4">{t("charts.cashflow.cash_flow_analysis")}</h3>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p>{t("charts.cashflow.no_data_available")}</p>
            <p className="text-sm mt-1">{t("charts.cashflow.no_data_complete_model")}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Annual Cash Flows */}
      <div className="bg-white rounded-xl shadow-sm border border-border p-6">
        <div className="mb-6">
          <h3 className="text-base font-semibold text-foreground mb-2">{t("charts.cashflow.cash_flow_analysis")}</h3>
          <p className="text-xs text-gray-500">{t("charts.cashflow.cash_flow_analysis_subtitle")}</p>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 30, left: 10, bottom: 5 }}
              barGap={2}
              barCategoryGap="8%"
            >
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="year" 
                tick={{ fontSize: 12 }}
                tickLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickLine={{ stroke: '#e2e8f0' }}
                tickFormatter={formatCurrencyShort}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                cursor={{ fill: 'rgba(10, 10, 10, 0.1)' }}
                formatter={(value: number, name: string) => [
                  formatCurrency(value), name
                  // BAR_NAMES[name] || name // Show the mapped name or fallback to the key
                ]}
                labelStyle={{ color: '#374151', fontWeight: 600 }}
              />
              <Legend 
                wrapperStyle={{ paddingTop: '20px' }}
                iconType="rect"
              />
              <Bar 
                dataKey="cashFlowLeveredPreTax" 
                name={t("charts.cashflow.series.cf_equity_pre_tax")}
                fill="#0A78FF"
                radius={[2, 2, 0, 0]}
              />
              <Bar 
                dataKey="cashFlowLeveredPostTax" 
                name={t("charts.cashflow.series.cf_equity_post_tax")}
                fill="#002E66"
                radius={[2, 2, 0, 0]}
              />
              <ReferenceLine y={0} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>



      {/* Cumulative Cash Flow */}
      <div className="bg-white rounded-xl shadow-sm border border-border p-6 pt-10">
        <div className="mb-6">
          <h3 className="text-base font-semibold mb-2">{t("charts.cashflow.cumulative_cash_flow")}</h3>
          <p className="text-xs text-gray-500">{t("charts.cashflow.cumulative_cash_flow_subtitle")}</p>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="year" 
                tick={{ fontSize: 12 }}
                tickLine={{ stroke: '#e2e8f0' }}
              />
              <YAxis 
                tick={{ fontSize: 12 }}
                tickLine={{ stroke: '#e2e8f0' }}
                tickFormatter={formatCurrencyShort}
              />
              <ReferenceLine y={0} />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                formatter={(value: number) => [formatCurrency(value), 'Value']}
                labelStyle={{ color: '#374151', fontWeight: 600 }}
              />
              <Line 
                type="linear" 
                dataKey="cumulativeCashFlow" 
                stroke="#002E66"
                strokeWidth={3}
                dot={{ fill: '#002E66', strokeWidth: 2, r: 4 }}
                activeDot={{ r: 6, stroke: '#002E66', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>        
      </div>
    </div>
  );
}